import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

import { getAuthCookie } from '@/utils/auth-cookie'
import { BROADCAST_URL, PUSHER } from '@/utils/constants'

export type AppEcho = Echo<'pusher'>

const bearer = (): string => {
  const token = getAuthCookie()
  return token ? `Bearer ${token}` : ''
}

/**
 * Realtime is opt-in: without VITE_ECHO_ENABLED=true no socket is opened, so a
 * checkout without Soketi running boots cleanly. Every consumer therefore has
 * to handle `null` — `laravelEcho?.private(...)`, never `laravelEcho.private`.
 *
 * Private channels authorise against EMR with the same Sanctum token the API
 * client sends, so the broadcasting auth endpoint sees the professional guard.
 */
function createEcho(): AppEcho | null {
  if (!PUSHER.enabled) return null

  // laravel-echo's pusher connector resolves the client off the global.
  ;(globalThis as unknown as { Pusher: typeof Pusher }).Pusher = Pusher

  return new Echo({
    broadcaster: 'pusher',
    key: PUSHER.key,
    cluster: PUSHER.cluster,
    wsHost: PUSHER.host,
    wsPort: PUSHER.port,
    wssPort: PUSHER.port,
    // A ws:// socket from an https:// page is blocked as mixed content, so the
    // page scheme decides unless the environment says otherwise.
    forceTLS: PUSHER.forceTLS || window.location.protocol === 'https:',
    disableStats: true,
    enabledTransports: ['ws', 'wss'],
    namespace: 'App\\Events\\Broadcast',
    authEndpoint: BROADCAST_URL,
    auth: { headers: { Authorization: bearer() } },
  })
}

/** Nullable singleton — see the note on `createEcho`. */
export const laravelEcho: AppEcho | null = createEcho()

/**
 * Login and logout change the token after Echo was built, and the connector
 * keeps its own copy of the auth headers, so both have to be rewritten.
 */
export function updateEchoAuthHeader(token: string | null): void {
  if (!laravelEcho) return

  const value = token ? `Bearer ${token}` : ''

  if (laravelEcho.options.auth?.headers) {
    laravelEcho.options.auth.headers.Authorization = value
  }

  const connector = laravelEcho.connector as unknown as {
    pusher?: { config: { auth?: { headers: Record<string, string> } } }
  }

  if (connector?.pusher?.config?.auth?.headers) {
    connector.pusher.config.auth.headers.Authorization = value
  }
}
